import { RuleGroupType } from 'react-querybuilder';
import { DashboardsState } from 'features/dashboardsState';
import { CalendarView } from 'components/Common/GranularityDateRangePicker/helper';

interface ApplicationState {
  toggle: boolean;
  toggleCount: number;
  notificationToggle: boolean;
  formDirtyState: boolean;
  applicationSettings: ApplicationSettings;
  searchText: string;
  sidebarItems: SidebarItem[];
  openedViews: any[];
  activeView: any;
  alert: AlertState;
  mapPopupState: MapPopupState;
}

interface SidebarItem {
  value: string;
  text: string;
  type: string;
  iconClass: string | null;
  accessModule: string | null;
  items: SidebarItem[]; // Recursive type for nested items
  properties?: any[];
  isExpanded: boolean;
  permissions: string[];
  favorite: boolean;
}

interface MapPopupState {
  isVisible: boolean;
  getGeoJsonCallback: Function;
  geoJson: any;
  region?: any;
}

interface RootState {
  applicationState: ApplicationState;
  notifications: Notifications;
  notificationCenter: NotificationCenter;
  dashboards: DashboardsState; // Add the DashboardsState slice
  advancedMap: AdvancedMapState;
  // Add more state slices for other features here
}

interface ChartAxisConfig {
  id: string;
  title: string;
  template: string | null;
  color: string | null;
}

interface AlertState {
  severity: 'success' | 'info' | 'warning' | 'error';
  title: string;
  message: string;
  open: boolean;
}

interface ApplicationSettings {
  BING_MAP_API_KEY: string;
  AUTHENTICATION_TYPE: string;
  HELP_LINK: string;
  FIRST_DAY_OF_WEEK: string;
  WEEK_CALC_RULE: string;
  MAX_VIEWS_OPEN: string;
  ENABLE_EXPORT_COMPRESSION: string;
  MAX_HIERARCHIES_LIMIT: string;
  CORRELATION_CONGESTION_ENABLED: boolean;
  DEFAULT_DATE_FORMAT: string;
  CHART_AXIS_CONFIG: ChartAxisConfig[];
  PRODUCT_VERSION: string;
  RELEASE_NOTES_LINK: string;
  SESSION_TIMEOUT: string;
  ALARM_MAX_NUMBER_OF_DISTINCT_RULE_ELEMENTS: number;
  ALARM_MAX_NUMBER_OF_ALARMS: number;
  ALARM_RETENTIONEXECUTIONLOGS_NUMBEROFPERIODS: RetentionExecutionLogsNumberOfPeriods;
}

interface RetentionExecutionLogsNumberOfPeriods {
  DEFAULT: number;
  '15min': number;
  hourly: number;
}

interface Notifications {
  notificationData: any;
}

interface NotificationCenter {
  export: FileInfo[];
  newExportSuccess: boolean;
  newExportFail: boolean;
}

interface FileInfo {
  Token: string;
  FileName: string;
  FriendlyName: string;
  CreateDate: string;
  ExpirationDate: string;
  DownloadCount: number;
  Target: string;
  UserId: string;
  Status: number;
  Details: string;
  isRead?: boolean;
}

interface FilterDataValue {
  id: string;
  name: string;
  items?: { id: string; name: any }[];
  contextInfo?: any;
  selected?: boolean;
}
interface FilterData {
  filterId: string;
  dashboardId?: string;
  podIds?: string[];
  group?: string;
  selectedValues?: FilterDataValue[];
  values?: FilterDataValue[];
  cascadeParentFilters?: string[];
  sourceType?: string;
  ready?: boolean;
  defaultValues?: string;
  label?: string;
  loaded?: boolean;
  type?: string;
  linkedPodIds?: string[];
  selectionType?: string;
  checked?: boolean;
  category?: string;
  maxSelectedValues?: number;
  maxSelectedValuesAlertMessage?: string;
}

interface GlobalDateFilter extends FilterData {
  //filterId: string;
  //values: { id: string; name: string; items?: { id: string; name: any }[] }[];
}

interface Dashboard {
  dashboardId: string;
  descriptor: DashboardDescriptor;
  isVisible: boolean;
  filters: {
    refreshPodIds: string[];
    maximizePodId: string | null;
    filters: FilterData[];
    forceRefresh?: boolean;
  };
}

type DashboardType = 'DASHBOARD';
type DashboardModule = 'DATAPLUGIN';
enum DashboardLayerType {
  CssStyleGeoJson = 'CssStyleGeoJson',
  SQM = 'SQM',
  KPI = 'KPI',
  GlobalFilterBasedCustomRenderer = 'GlobalFilterBasedCustomRenderer',
  CssStyleGeoJsonGDBFileBased = 'CssStyleGeoJsonGDBFileBased',
}

interface DashboardLayerProperties {
  LegendValueToColorMapping?: { [key: string]: any };
}

interface DashboardLayerConfig {
  LayerId: string;
  LayerName?: string;
  LayerType?: DashboardLayerType;
  LayerProperties?: DashboardLayerProperties;
  TargetElementSupportedTechs?: string[];
  TargetElementSupportedLevels?: string[];
  selected?: boolean;
  isLoading?: boolean;
  MinZoomLevelSupported?: number | null;
  SidePanelListModeDisplay: boolean | false;
}
interface DashboardDescriptorSettings {
  technologies: { [key: string]: any };
  cellsForHighlight: string[];
  elementDetailsLayersConfig: DashboardLayerConfig[];
  technologyLayerConfig: DashboardLayerConfig[];
  sqmLayersConfig: DashboardLayerConfig[];
  globalLayersConfig: DashboardLayerConfig[];
}

interface DashboardProperties {
  Name: string;
  Value: string;
}

interface DashboardDescriptor {
  dashboardId: string;
  title: string;
  type: DashboardType;
  parentItemId: number;
  module: DashboardModule;
  position: number;
  permissions: string[];
  settings: DashboardDescriptorSettings;
  properties: DashboardProperties[];
  filters: FilterData[];
  isVisible: boolean;
  pods: any[];
}

interface AlarmDefinition {
  id?: number;
  name: string;
  description: string | null;
  dsp_id: string;
  severity: string;
  enabled: boolean;
  consecutive_periods?: number;
  execution_group: string;
  execution_priority: string;
  alarm_expiration: string | null;
  alarm_rule_serialized?: RuleGroupType;
  time_aggregation_level: string;
  target_element_criteria_serialized?: RuleGroupType | null;
  object_aggregation_level?: string;
  createdUser?: string | null;
  createdDatetime?: string | null;
  lastUpdatedUser?: string | null;
  lastUpdatedDatetime?: number | null;
  study_intervals?: number | null;
  data_profile_sid?: number | null;
  output_serialized?: AlarmDefinitionOutput;
  execution_rules_serialized?: AlarmExecutionRules;
  latest_evaluated_period?: string | null;
  latest_execution?: string | null;
  total_active_alarm_instances?: number | null;
  breachingPeriods?: string | null;
}

interface AlarmExecutionRules {
  weekendsExcluded: boolean;
  useDayOverDay?: boolean;
  useBusyHourKPIValue?: boolean;
  instancesCreationPeriodCriteria?: InstancesCreationPeriodCriteria;
}

interface InstancesCreationPeriodCriteria {
  numberOfBreachedInterval: number;
  numberOfLatestIntervals: number;
}

interface AlarmDefinitionOutput {
  notificationType?: string;
  subject?: string;
  recipients?: EmailRecipients;
  attributes?: ValueLabelGroup[];
}

interface AlarmRulesCount {
  AllAlarms: number;
  ActiveAlarms: number;
  PausedAlarms: number;
}

interface AlarmMetadata {
  severity: string[];
  executionGroup: string[];
  executionPriority: IdNameModel[];
  consecutivePeriod: string[];
  notificationType: string[];
}

interface IdNameModel {
  id: string;
  name: string;
}

interface EmailRecipients {
  to: string[];
  cc: string[];
}

interface HierarchyLevelMetadata {
  index: number;
  id: string;
  name: string;
  aiThresholdEnabled: boolean;
}

interface DimAttributesMetadata {
  id: string;
  name: string;
  label: string;
  minimalApplicationLevel: string;
  hierarchyLevelId: string;
  dataType?: string;
  attributeType?: string;
}

interface KPIMetadata {
  contextInfo: any | null;
  id: string;
  items: any[] | null;
  name: string;
}

interface PIMetadata {
  contextInfo: any | null;
  id: string;
  items: any[] | null;
  name: string;
}

interface KQIMetadata {
  contextInfo: any | null;
  id: string;
  items: any[] | null;
  name: string;
}

interface VendorCountersMetadata {
  contextInfo: any | null;
  id: string;
  items: any[] | null;
  name: string;
}

interface AlarmRulesMetadata {
  KPIs: KPIMetadata[];
  PIs: PIMetadata[];
  KQIs: KQIMetadata[];
  CustomKPIs: PIMetadata[];
  DimAttributes: DimAttributesMetadata[];
  VendorCounters: VendorCountersMetadata[];
}
interface HierarchyLevel {
  IncludeParentIds: boolean;
  LevelIdAttr: any;
  LevelIndex: number;
  LevelNameAttr: any;
}
interface Hierarchy {
  HierarchyId: string;
  HierarchyInitLevel: number;
  HierarchyLevels: HierarchyLevel[];
  HierarchyMaxmimumLevel: number;
  Name: string;
}

interface DataSourceAlarmMetadata {
  hierarchyLevels: HierarchyLevelMetadata[];
  kpis: KPIMetadata[];
  pis: PIMetadata[];
  kqis: KQIMetadata[];
  customKpis: KPIMetadata[];
  hierarchies: { [key: string]: Hierarchy };
  dimAttributes: DimAttributesMetadata[];
  vendorCounters: VendorCountersMetadata[];
  supportedGranularities: string[];
}

interface LayerProperties {
  layerId: string;
  viewPort: LayerCoordinates;
}

interface LayerCoordinates {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

interface PreDefinedGranularityFilterOption {
  label: string;
  value: string;
  calendarView: CalendarView;
  showSpecificAndCustomFilters: boolean;
  maxDays?: number;
  hiddenFilterOptions: string[];
  defaultValue: string;
}

interface CalendarFilterOption {
  label: string;
  value: string;
  getDates: () => { dateFrom: Date; dateTo: Date };
}

interface LayerDataRequest {
  layerId: string;
  Viewport: LayerCoordinates;
  limit: number;
  offset: number;
}

interface LayerDataCount {
  geoJSONData: number;
}

interface DataProfile {
  profileSid: number;
  sourceDataElementId: string;
  name: string;
  profileType: string;
  rules: any;
  createdDateTime: string;
  lastUpdatedDateTime: string;
  description: string;
  profileSource: string;
  visibility: string;
  createdUser: string;
  elementsList: string[];
  dataElementLevelId: string;
  assignedUsers: string[];
  groupId?: number;
  parentGroupId?: number;
  parentGroupName: string;
  groupName: string;
  listElementId: string;
  filterEnabled: boolean;
}

interface QueryPreviewType {
  contextInfo: any;
  totalRowCount: number;
  data: { [key: string]: string }[];
  metadata: QueryPreviewMetadata[];
}

interface QueryPreviewMetadata {
  absoluteSorting?: boolean;
  columnId?: string;
  columnIndex?: number;
  columnName?: string;
  columnType?: string;
  contextInfo?: any;
  fromColumnMenu?: boolean;
  isHidden?: boolean;
}

interface QueryBuilderValidation {
  hasKPIorPIorVendorCounter: boolean;
  hasEmptyValue: boolean;
}

interface DateRangePickerFilterVariables {
  selectedGranularity?: string;
  selectedFilterOption?: string;
  customValues?: { daysFrom: number; daysTo: number };
  specificDataValues?: { value: number }[];
  customDataValues?: {
    startTime: { hour: number; minute: number };
    endTime: { hour: number; minute: number };
  };
  calendarDates?: Date[];
}

interface ValueLabelGroup {
  group: string;
  items: ValueLabel[];
}

interface ValueLabel {
  index: number;
  value: string;
  label: string;
  enableDeleteButton: boolean;
}

interface SelectedQueryBuilderField {
  targetCriteria: string[];
  rules: string[];
}

interface IdValue {
  id: string;
  value: string;
}
interface AdvancedMapState {
  zoom: number; // Include dashboardId in each filter
  center: number[];
  selectedBands: string[];
  granularityDates: any;
  globalLayers: DashboardLayerConfig[];
  globalLayersLoaded: boolean;
  mapStyles: {
    options: MapStyleType[],
    selected: MapStyleType,
  }
  bandsList: any[];
  clickedCell: any;
  highlightedCriterias: IdValue[];
}

type LayerLegendShape = 'circle' | 'square';

interface DataProfileRuleElement {
  field: string;
  id: string;
  operator: string;
  type: string;
  value: any;
  data: {
    attributeType: string;
  };
  input?: string;
}

interface DataProfileRule {
  condition: string;
  field: string | null;
  id: string | null;
  operator: string | null;
  rules: DataProfileRuleElement[];
  type: string | null;
  value: string | null;
  data: any;
}

type MapStyleType = 'Standard' | 'Humanitarian' | 'Bing Grey';

interface TroubleTicket {
  TicketNumber: string;
  ProductIdentifier: string;
  NIDTCode: string;
  ClientImpact: string;
  ResourceTypeIdentifier: string;
  TicketType: string;
  TreatmentPriority: string;
  Status: string;
  Comment: string;
  CommentDate: Date;
  CreationDate: string;
  TicketClosureDate: string;
  TicketStartDate: string;
  Symptom: string;
  Origin: string;
  FinalNature: string;
  BriefDescription: string;
  ImputationDescription: string;
  TechnicalImpact: string;
  ProblemFamily: string;
  SymptomFamily: string;
  TicketRestorationDate: string;
  ProblemDetails: string;
}
interface Suggestion {
  suggestionItem: any;
  label: string;
  type: 'cell' | 'site' | 'mapPlace' | 'geoJsonFeature';
  // this is required when the suggestion is a site
  // since the suggestionItem has the "location" marker info
  // so this index gives the index of the site withint the siteMarkerInfo.sites
  siteIndex?: number; 
}
interface SearchGroup {
  cells: Suggestion[];
  sites: Suggestion[];
  mapPlaces: Suggestion[];
  geoJsonFeatures: Suggestion[];
}

interface CustomGeoJsonLayerData {
  id: string;
  name: string;
  features: any[];
}


interface IDataProviderService {
  getFilterData: (filterRequest: FilterRequest) => Promise<any>;
  getData: (requestData: any) => Promise<any>;
  getDashboardDescriptor: (dashboardId: string) => Promise<any>;
  getAdvancedMapData: (filterRequest: MapDataRequest) => Promise<any>;
  getAdvancedMapDataFilters: (requestData: any) => Promise<any>;
}

interface FilterRequest {
  userId: string;
  dashboardId: string;
  podId: string;
  filterId: string;
  filters: any[];
}
interface MapDataRequest {
  dashboardId: string;
  podId: string;
  viewPort?: LatLng[];
  exclusionViewPort?: LatLng[];
  zoomLevel?: number;
  layerIds?: string[];
  dataFilters?: FilterData[]
}

interface LatLng {
  lat: number;
  lng: number;
}

interface MapGeoJsonDataResult {
  data?: any;
  contextInfo?: {
    [key: string]: object;
  };
  combinedData?: any;
  maxSubscribers?: number;
}

export type {
  ApplicationState,
  AlertState,
  RootState,
  Notifications,
  SidebarItem,
  ApplicationSettings,
  FileInfo,
  NotificationCenter,
  AlarmDefinition,
  AlarmRulesCount,
  FilterData,
  FilterDataValue,
  Dashboard,
  HierarchyLevelMetadata,
  DataSourceAlarmMetadata,
  DimAttributesMetadata,
  KPIMetadata,
  PIMetadata,
  VendorCountersMetadata,
  AlarmMetadata,
  IdNameModel,
  AlarmRulesMetadata,
  MapPopupState,
  LayerProperties,
  LayerCoordinates,
  PreDefinedGranularityFilterOption,
  CalendarFilterOption,
  GlobalDateFilter,
  LayerDataRequest,
  LayerDataCount,
  DataProfile,
  QueryPreviewType,
  QueryPreviewMetadata,
  ValueLabelGroup,
  ValueLabel,
  Hierarchy,
  HierarchyLevel,
  QueryBuilderValidation,
  DateRangePickerFilterVariables,
  SelectedQueryBuilderField,
  AdvancedMapState,
  MapStyleType,
  DataProfileRuleElement,
  DataProfileRule,
  IdValue,
  TroubleTicket,
  AlarmExecutionRules,
  EmailRecipients,
  DashboardDescriptor,
  DashboardDescriptorSettings,
  DashboardModule,
  DashboardType,
  DashboardProperties,
  DashboardLayerConfig,
  DashboardLayerProperties,
  LayerLegendShape,
  SearchGroup,
  Suggestion,
  CustomGeoJsonLayerData,
  IDataProviderService,
  FilterRequest,
  MapDataRequest,
  LatLng,
  MapGeoJsonDataResult,
};
export { DashboardLayerType };

